import React, { useEffect, useRef, useState } from "react";
import { withRouter } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import { RegistrationLayout } from "../../../RegistrationLayout/RegistrationLayout";
import { RegistrationSetupNavbar } from "../../../RegistrationLayout/RegistrationSetupNavbar";
import RegistrationSetupWrapper from "../../RegistrationSetup.style";
import {
  REGISTRATION_SETUP_STEP_META,
  REGISTRATION_SETUP_TOTAL_STEPS,
} from "../../../../helper/constants";
import AuthenticationApiSerivce from "../../../../api/AuthServices";
import {
  isRegistrationLoadRetryExhaustedError,
  isRequestCancelledError,
} from "../../../../api/requestRetry";
import {
  getRegistrationData,
  isCardOnFileComplete,
  updateRegistrationData,
  clearNoPreferenceRegistrationState,
} from "../../registrationSetupStorage";
import {
  buildListMatchedRdPayload,
  getEarliestNextAvailable,
  isInPersonModality,
  isTelehealthModality,
  mapMatchedRdListToSelectionCards,
} from "../registrationMatchedRd";
import RegistrationRdAvatar from "../RegistrationRdAvatar";
import { registrationStepBack } from "../../registrationNavigation";
import { trackRegistrationProviderSelected } from "../../../../helper/registrationAnalytics";
import RegistrationLoadRetryModal from "../../RegistrationLoadRetryModal";
import {
  redirectIfNoProviderFromListMatchedRdResponse,
  redirectIfProviderUnavailableBeforeApi,
} from "../../registrationRdAvailability";

const DIETITIAN_STEP = REGISTRATION_SETUP_STEP_META.DIETITIAN_SELECTION;

const getRdListEyebrowLabel = (translate) => {
  const stepLabel = translate("NUTRITION_COMMON_LBL_STEP_OF", {
    current: DIETITIAN_STEP.step,
    total: REGISTRATION_SETUP_TOTAL_STEPS,
  });
  const eyebrow = translate("NUTRITION_RDLIST_LBL_EYEBROW");
  const separatorIndex = eyebrow.indexOf("—");
  const suffix =
    separatorIndex === -1 ? eyebrow : eyebrow.slice(separatorIndex + 1).trim();
  return `${stepLabel} — ${suffix}`.toUpperCase();
};

const translateRdTag = (tag, translate) => {
  if (isTelehealthModality(tag)) {
    return translate("NUTRITION_COMMON_LBL_TELEHEALTH");
  }
  if (isInPersonModality(tag)) {
    return translate("NUTRITION_COMMON_LBL_IN_PERSON");
  }
  return tag;
};

const RegistrationDietitianSelection = ({ history }) => {
  const { t, i18n } = useTranslation();
  const [dietitians, setDietitians] = useState([]);
  const [matchedRdList, setMatchedRdList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasListMatchedRdResponse, setHasListMatchedRdResponse] = useState(false);
  const [loadRetryModal, setLoadRetryModal] = useState({
    isOpen: false,
    onRetry: null,
  });
  const loadMatchedDietitiansRef = useRef(null);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevRootHeight = root?.style.height ?? "";

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    if (root) {
      root.style.height = "100vh";
      root.style.height = "100dvh";
    }

    const saved = localStorage.getItem("data");
    if (!saved) {
      history.replace("/register/account/payment");
      return () => {
        html.style.overflow = prevHtmlOverflow;
        body.style.overflow = prevBodyOverflow;
        if (root) root.style.height = prevRootHeight;
      };
    }
    try {
      const parsed = JSON.parse(saved);
      if (!isCardOnFileComplete(parsed)) {
        history.replace("/register/account/card");
      }
    } catch {
      history.replace("/register/account/payment");
    }

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      if (root) {
        root.style.height = prevRootHeight;
      }
    };
  }, [history]);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    const loadMatchedDietitians = async () => {
      setIsLoading(true);
      setHasListMatchedRdResponse(false);
      setLoadRetryModal({ isOpen: false, onRetry: null });
      let shouldRevealPage = false;
      try {
        const registrationData = getRegistrationData();

        if (redirectIfProviderUnavailableBeforeApi(history, registrationData)) {
          return;
        }

        const payload = buildListMatchedRdPayload(registrationData);

        if (!payload.category_id) {
          message.error(t("NUTRITION_RDLIST_ERR_MISSING_DETAILS"));
          history.replace("/register/account");
          return;
        }

        const res = await AuthenticationApiSerivce.listRegistrationMatchedRd(
          payload,
          i18n.language,
          { signal: controller.signal }
        );

        if (cancelled) {
          return;
        }

        if (redirectIfNoProviderFromListMatchedRdResponse(history, res)) {
          return;
        }

        if (!res?.status) {
          message.error(res?.message || t("NUTRITION_RDLIST_ERR_LOAD"));
          setMatchedRdList([]);
          shouldRevealPage = true;
          return;
        }

        const list = Array.isArray(res?.data) ? res.data : [];
        updateRegistrationData({ matchedRdList: list });
        setMatchedRdList(list);
        shouldRevealPage = true;
      } catch (err) {
        if (cancelled || isRequestCancelledError(err)) {
          return;
        }
        if (isRegistrationLoadRetryExhaustedError(err)) {
          setMatchedRdList([]);
          setLoadRetryModal({
            isOpen: true,
            onRetry: () => loadMatchedDietitiansRef.current?.(),
          });
          shouldRevealPage = true;
          return;
        }
        message.error(
          err?.response?.data?.message ||
            err?.message ||
            t("NUTRITION_RDLIST_ERR_LOAD")
        );
        setMatchedRdList([]);
        shouldRevealPage = true;
      } finally {
        if (!cancelled && shouldRevealPage) {
          setHasListMatchedRdResponse(true);
          setIsLoading(false);
        }
      }
    };

    loadMatchedDietitiansRef.current = loadMatchedDietitians;
    loadMatchedDietitians();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [history, i18n.language, t]);

  useEffect(() => {
    setDietitians(
      mapMatchedRdListToSelectionCards(
        matchedRdList,
        getEarliestNextAvailable(matchedRdList),
        t
      )
    );
  }, [matchedRdList, t]);

  const handleSelect = (rd) => {
    if (rd.isNoPreference) {
      updateRegistrationData({
        selectedDietitian: {
          id: "no-preference",
          name: t("NUTRITION_RDLIST_VAL_NO_PREFERENCE"),
          noPreference: true,
        },
        selectedAppointment: null,
        noPreferenceSelectedSlot: null,
        noPreferenceSlotData: null,
        noPreferenceRdList: null,
        noPreferenceSelectedRd: null,
      });
      trackRegistrationProviderSelected("no-preference");
      history.push("/register/account/no-preference/appointments");
      return;
    }
    updateRegistrationData({
      ...clearNoPreferenceRegistrationState(),
      selectedDietitian: {
        id: rd.id,
        name: rd.name,
        noPreference: false,
        raw: rd.raw,
      },
    });
    trackRegistrationProviderSelected(rd.id);
    history.push(`/register/account/dietitians/${rd.id}`);
  };

  if (!hasListMatchedRdResponse) {
    return null;
  }

  return (
    <RegistrationSetupWrapper>
      <RegistrationLayout
        containScroll
        header={
          <RegistrationSetupNavbar
            stepMeta={DIETITIAN_STEP}
            totalSteps={REGISTRATION_SETUP_TOTAL_STEPS}
            onBack={() => registrationStepBack(history)}
          />
        }
      >
        <div className="scroll-body scroll-body-rd">
          <div className="content-card content-card-rd">
            <div className="content-card-scroll content-card-scroll-rd">
              <div className="rd-list-intro">
                <div className="section-eyebrow rd-list-step">
                  {getRdListEyebrowLabel(t)}
                </div>
                <h2 className="section-title rd-list-title">
                  {t("NUTRITION_RDLIST_LBL_TITLE")}
                </h2>
                <p className="section-sub rd-list-sub">
                  {t("NUTRITION_RDLIST_DESC_SUBTITLE")}
                </p>
              </div>

              <div className="rd-list">
                {dietitians.map((rd) => (
                    <button
                      key={rd.id}
                      type="button"
                      className={
                        rd.isNoPreference ? "rd-card rd-card-no-pref" : "rd-card"
                      }
                      onClick={() => handleSelect(rd)}
                    >
                      {rd.isNoPreference ? (
                        <div
                          className="rd-avatar"
                          style={{ background: rd.avatarStyle }}
                        >
                          {rd.initials}
                        </div>
                      ) : (
                        <RegistrationRdAvatar
                          className="rd-avatar"
                          profileImage={rd.profileImage}
                          initials={rd.initials}
                          avatarStyle={rd.avatarStyle}
                          alt={rd.name}
                        />
                      )}
                      <div className="rd-info">
                        <div
                          className={
                            rd.isNoPreference ? "rd-name rd-name-pref" : "rd-name"
                          }
                        >
                          {rd.name}
                        </div>
                        {!rd.isNoPreference && rd.creds ? (
                          <div className="rd-creds">{rd.creds}</div>
                        ) : null}
                        {rd.isNoPreference && (
                          <>
                            <div className="rd-creds">{rd.creds}</div>
                            <p className="rd-pref-desc">{rd.description}</p>
                            {rd.nextAvailable ? (
                              <div className="rd-meta">
                                <div className="rd-next-available">
                                  {t("NUTRITION_RDLIST_LBL_NEXT_AVAILABLE")}{" "}
                                  <strong>{rd.nextAvailable}</strong>
                                </div>
                              </div>
                            ) : null}
                          </>
                        )}
                        {!rd.isNoPreference && rd.tags.length > 0 && (
                          <div className="rd-tags">
                            {rd.tags.map((tag) => (
                              <span key={tag} className="rd-tag">
                                {translateRdTag(tag, t)}
                              </span>
                            ))}
                          </div>
                        )}
                        {!rd.isNoPreference && (
                          <>
                            {rd.states ? (
                              <div className="rd-states">
                                {t("NUTRITION_RDLIST_LBL_LICENSED_IN")}{" "}
                                <strong>{rd.states}</strong>
                              </div>
                            ) : null}
                            {rd.about ? (
                              <p className="rd-about-preview">{rd.about}</p>
                            ) : null}
                            {rd.nextAvailable ? (
                              <div className="rd-meta">
                                <div className="rd-next-available">
                                  {t("NUTRITION_RDLIST_LBL_NEXT_AVAILABLE")}{" "}
                                  <strong>{rd.nextAvailable}</strong>
                                </div>
                              </div>
                            ) : null}
                          </>
                        )}
                      </div>
                      <span className="rd-arrow" aria-hidden>
                        ›
                      </span>
                    </button>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </RegistrationLayout>

      <RegistrationLoadRetryModal
        isOpen={loadRetryModal.isOpen}
        onRetry={() => {
          const retry = loadRetryModal.onRetry;
          setLoadRetryModal({ isOpen: false, onRetry: null });
          retry?.();
        }}
        onCancel={() => setLoadRetryModal({ isOpen: false, onRetry: null })}
        isRetrying={isLoading}
      />
    </RegistrationSetupWrapper>
  );
};

export default withRouter(RegistrationDietitianSelection);
